import React, { useState, useEffect } from "react";
import { auth, db } from "../firebase";
import { ref, onValue, get } from "firebase/database";
import { useNavigate } from "react-router-dom";
import DashboardHome from "../components/DashboardHome";
import Teams from "../components/Teams";
import TeamDetails from "../components/TeamDetails";
import BiddingPanel from "../components/BiddingPanel";
import AuctionLog from "../components/AuctionLog";
import Popup from "../components/Popup";

const OrganizerDashboard = () => {
  const navigate = useNavigate();
  const [gameId, setGameId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [game, setGame] = useState(null);
  const [popup, setPopup] = useState({ isOpen: false, title: '', message: '', type: 'info' });

  useEffect(() => {
    if (!gameId) return;

    const gameRef = ref(db, `games/${gameId}`);
    const unsubscribe = onValue(gameRef, (snap) => {
      if (snap.exists()) {
        setGame(snap.val());
      } else {
        setGame(null);
      }
    });

    return () => unsubscribe();
  }, [gameId]);

  const getUserTeamId = () => {
    if (!game || !auth.currentUser || !game.teams) return null;

    const teamEntry = Object.entries(game.teams).find(
      ([teamId, team]) => team.bidderUid === auth.currentUser.uid
    );

    return teamEntry ? teamEntry[0] : null;
  };

  const userTeamId = getUserTeamId();
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (!user) {
        navigate("/login");
        return;
      }

      try {
        const gameIdRef = ref(db, `users/${user.uid}/gameId`);
        const snapshot = await get(gameIdRef);

        if (snapshot.exists()) {
          setGameId(snapshot.val());
        } else {
          setPopup({
            isOpen: true,
            title: 'No Game Found',
            message: "No game ID found for this organizer.",
            type: 'warning'
          });
        }
      } catch (error) {
        console.error("Error fetching game ID:", error);
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  if (loading) {
    return (
      <div className="container mt-5 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!gameId) {
    return (
      <div className="container mt-5">
        <div className="alert alert-warning">
          <i className="bi bi-exclamation-triangle me-2"></i>
          No game found. Please contact support.
        </div>
      </div>
    );
  }

  return (
    <div className="min-vh-100" >
      <div className="container-fluid pt-2 px-4">
        <div className="card shadow-lg border-0" style={{ borderRadius: "20px", overflow: "hidden" }}>
          {/* Hero Banner */}
          <div className="card-header text-white text-center py-4" style={{ background: " #667eea " }}>
            <h1 className="h2 fw-bold mb-2">
              <i className="bi bi-speedometer2 me-2"></i>
              Owner Dashboard
            </h1>
            <p className="mb-0">Manage your team and participate in the auction</p>
          </div>

          <div className="card-body p-4">
            {/* Tab Navigation */}
            <ul className="nav nav-pills mb-4 justify-content-center" style={{ gap: "10px" }}>
              <li className="nav-item">
                <button
                  className={`nav-link ${activeTab === "overview" ? "active" : ""}`}
                  onClick={() => setActiveTab("overview")}
                  style={{
                    borderRadius: "50px",
                    padding: "12px 30px",
                    fontWeight: "600",
                    background: activeTab === "overview" ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" : "transparent",
                    border: activeTab === "overview" ? "none" : "2px solid #dee2e6",
                    color: activeTab === "overview" ? "white" : "#6c757d"
                  }}
                >
                  <i className="bi bi-info-circle me-2"></i>
                  Overview
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`nav-link ${activeTab === "myteam" ? "active" : ""}`}
                  onClick={() => setActiveTab("myteam")}
                  style={{
                    borderRadius: "50px",
                    padding: "12px 30px",
                    fontWeight: "600",
                    background: activeTab === "myteam" ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" : "transparent",
                    border: activeTab === "myteam" ? "none" : "2px solid #dee2e6",
                    color: activeTab === "myteam" ? "white" : "#6c757d"
                  }}
                >
                  <i className="bi bi-shield me-2"></i>
                  My Team
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`nav-link ${activeTab === "teams" ? "active" : ""}`}
                  onClick={() => setActiveTab("teams")}
                  style={{
                    borderRadius: "50px",
                    padding: "12px 30px",
                    fontWeight: "600",
                    background: activeTab === "teams" ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" : "transparent",
                    border: activeTab === "teams" ? "none" : "2px solid #dee2e6",
                    color: activeTab === "teams" ? "white" : "#6c757d"
                  }}
                >
                  <i className="bi bi-people me-2"></i>
                  Teams & Players
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`nav-link ${activeTab === "auctioncontrol" ? "active" : ""}`}
                  onClick={() => setActiveTab("auctioncontrol")}
                  style={{
                    borderRadius: "50px",
                    padding: "12px 30px",
                    fontWeight: "600",
                    background: activeTab === "auctioncontrol" ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" : "transparent",
                    border: activeTab === "auctioncontrol" ? "none" : "2px solid #dee2e6",
                    color: activeTab === "auctioncontrol" ? "white" : "#6c757d"
                  }}
                >
                  <i className="bi bi-broadcast me-2"></i>
                  Auction
                </button>
              </li>
            </ul>

            {/* Tab Content */}
            <div className="tab-content">
              {activeTab === "overview" && (
                <div className="animate-fade-in">
                  <DashboardHome gameId={gameId} />
                </div>
              )}
              {activeTab === "myteam" && (
                <div className="animate-fade-in">
                  <TeamDetails gameId={gameId} teamId={userTeamId} />
                </div>
              )}
              {activeTab === "auctioncontrol" && (
                <div className="row animate-fade-in">
                  <div className="col-lg-9 mb-4">
                    <BiddingPanel 
                      gameId={gameId} 
                      teamId={userTeamId} 
                      teamName={game?.teams?.[userTeamId]?.teamName || ""}
                    />
                  </div>
                  <div className="col-lg-3 mb-4">
                    { game?.details.status === "live" ? (<AuctionLog gameId={gameId} />
                    ) : 
                    (<div className="alert alert-info">
                      <i className="bi bi-info-circle me-2"></i>
                      Auction is not active. Activity log will be available once the auction starts.
                    </div>)}
                  </div>
                </div>
              )}
              {activeTab === "teams" && (
                <div className="animate-fade-in">
                  <Teams gameId={gameId} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .animate-fade-in {
          animation: fadeIn 0.5s ease-in;
        }
        
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .nav-link {
          transition: all 0.3s ease;
        }

        .nav-link:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }
      `}</style>

      <Popup
        isOpen={popup.isOpen}
        title={popup.title}
        message={popup.message}
        type={popup.type}
        onClose={() => setPopup({ ...popup, isOpen: false })}
      />
    </div>
  );
};

export default OrganizerDashboard;
